import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import "../styles/HomeNew.css";
import projectData from "../data/projectData.json";
import parse from "html-react-parser";

const G_META = {
    "solidarity-engineering": { focus: "Clean Water / Health", region: "Americas", color: "lchip--purple", dot: "dot--purple" },
    "action-in-africa": { focus: "Education", region: "Uganda", color: "lchip--pink", dot: "dot--pink" },
    "mali-rising": { focus: "Girls' Education", region: "West Africa", color: "lchip--green", dot: "dot--green" },
    "connected": { focus: "Education Access", region: "Central America", color: "lchip--purple", dot: "dot--purple" },
    "el-porvenir": { focus: "Clean Water", region: "Nicaragua", color: "lchip--pink", dot: "dot--pink" },
    "maya-book-project": { focus: "Literacy", region: "Guatemala", color: "lchip--green", dot: "dot--green" },
    "safe-passage": { focus: "Health / Education", region: "Guatemala", color: "lchip--purple", dot: "dot--purple" },
    "yabal": { focus: "Women Empowerment", region: "Guatemala", color: "lchip--pink", dot: "dot--pink" },
    "ihf": { focus: "Child Education", region: "Guatemala", color: "lchip--green", dot: "dot--green" },
    "kyc": { focus: "Youth Health", region: "Kenya", color: "lchip--purple", dot: "dot--purple" },
};

const L_META = {
    "b2s": { focus: "Education", color: "lchip--purple", dot: "dot--purple" },
    "one-hope-project": { focus: "Health", color: "lchip--pink", dot: "dot--pink" },
    "ywca": { focus: "Equity", color: "lchip--green", dot: "dot--green" },
    "creative-healing": { focus: "Mental Health", color: "lchip--purple", dot: "dot--purple" },
    "bgc": { focus: "Youth", color: "lchip--pink", dot: "dot--pink" },
    "art-station": { focus: "Arts", color: "lchip--green", dot: "dot--green" },
    "sunnyside-community-garden": { focus: "Food Security", color: "lchip--purple", dot: "dot--purple" },
    "charm": { focus: "Community", color: "lchip--pink", dot: "dot--pink" },
    "project-oz": { focus: "Youth Services", color: "lchip--green", dot: "dot--green" },
    "dreams-are-possible": { focus: "Community", color: "lchip--purple", dot: "dot--purple" },
    "path": { focus: "Crisis Support", color: "lchip--pink", dot: "dot--pink" },
};

export default function Project() {
    const { name } = useParams();

    const project = projectData.projects.find(
        p => p.uriName.localeCompare(name) === 0
    );

    // Graceful fallback if project not found
    if (!project) {
        return (
            <div className="pg" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ textAlign: "center" }}>
                    <p className="hero__kicker">404</p>
                    <h2 style={{ fontFamily: "var(--ff-head)", fontSize: "2rem", fontWeight: 800, color: "var(--ink)", marginBottom: "1rem" }}>
                        Project not found
                    </h2>
                    <Link to="/projects" className="cta cta--solid" style={{ background: "var(--pu-700)", color: "var(--white)" }}>
                        ← Back to Projects
                    </Link>
                </div>
            </div>
        );
    }

    const isGlobal = project.type === "G";
    const meta = isGlobal
        ? (G_META[project.uriName] || { focus: "Philanthropy", region: "Global", color: "lchip--purple", dot: "dot--purple" })
        : (L_META[project.uriName] || { focus: "Community", region: "Illinois", color: "lchip--purple", dot: "dot--purple" });

    const accentVar = meta.color === "lchip--purple" ? "var(--pu-500)"
        : meta.color === "lchip--pink" ? "var(--pk-500)"
            : "var(--gn-500)";
    const bgVar = meta.color === "lchip--purple" ? "var(--pu-50)"
        : meta.color === "lchip--pink" ? "var(--pk-50)"
            : "var(--gn-50)";
    const borderVar = meta.color === "lchip--purple" ? "var(--pu-200)"
        : meta.color === "lchip--pink" ? "var(--pk-200)"
            : "var(--gn-200)";

    // Gather extra photos (pic1, pic2, … up to pic9)
    const photos = [];
    for (let i = 1; i <= 9; i++) {
        const pic = project[`pic${i}`];
        const desc = project[`desc${i}`];
        if (pic) photos.push({ src: `/images/projects/${pic}`, caption: desc });
    }

    return (
        <div className="pg">

            {/* ── HERO ── */}
            <section className="hero" style={{ minHeight: "44svh" }}>
                <div className="hero__glow hero__glow--1" />
                <div className="hero__glow hero__glow--2" />
                <div className="hero__glow hero__glow--3" />

                <div className="hero__inner" style={{ gridTemplateColumns: "1fr auto", alignItems: "center" }}>
                    <div className="hero__text">
                        {/* Breadcrumb */}
                        <p className="hero__kicker" style={{ marginBottom: ".6rem" }}>
                            <Link
                                to="/projects"
                                style={{ color: "var(--pu-200)", textDecoration: "none", fontWeight: 700 }}
                            >
                                ← All Projects
                            </Link>
                        </p>

                        {/* Badges */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginBottom: "1rem" }}>
                            <span className={`lcard__chip ${meta.color}`} style={{ fontSize: ".72rem" }}>
                                {meta.focus}
                            </span>
                            {isGlobal && (
                                <span className="lcard__chip lchip--purple" style={{ fontSize: ".72rem", background: "rgba(167,139,250,.2)", color: "#c4b5fd", border: "1px solid rgba(167,139,250,.3)" }}>
                                    🌍 {meta.region}
                                </span>
                            )}
                            <span className="lcard__chip" style={{ fontSize: ".72rem", background: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.6)", border: "1px solid rgba(255,255,255,.15)" }}>
                                {isGlobal ? "Global Project" : "Local Project"}
                            </span>
                        </div>

                        <h1 className="hero__h1" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
                            {project.name}
                        </h1>

                        <p className="hero__sub" style={{ maxWidth: "560px" }}>
                            {project.shortDesc}
                        </p>

                        <div className="hero__ctas">
                            {project.uri && (
                                <a href={project.uri} target="_blank" rel="noopener noreferrer" className="cta cta--solid">
                                    Visit Website ↗
                                </a>
                            )}
                            {photos.length > 0 && (
                                <a href="#gallery" className="cta cta--ghost">See Photos ↓</a>
                            )}
                        </div>
                    </div>

                    {/* Logo card */}
                    <div style={{
                        background: "rgba(255,255,255,.1)",
                        border: "1px solid rgba(255,255,255,.15)",
                        borderRadius: "20px",
                        padding: "1.5rem",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "1rem",
                        backdropFilter: "blur(8px)",
                    }}>
                        <img
                            src={`/images/projects/${project.logo}`}
                            alt={project.name}
                            width={140}
                            height={140}
                            style={{ borderRadius: "14px", objectFit: "cover", display: "block" }}
                            onError={e => (e.target.style.display = "none")}
                        />
                        {project.afs && (
                            <div style={{ textAlign: "center" }}>
                                <span style={{
                                    display: "block",
                                    fontSize: ".62rem",
                                    fontWeight: 700,
                                    letterSpacing: ".12em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,.4)",
                                    marginBottom: ".2rem",
                                }}>
                                    Approved Support
                                </span>
                                <span style={{
                                    fontFamily: "var(--ff-head)",
                                    fontSize: "1.4rem",
                                    fontWeight: 800,
                                    color: "#c4b5fd",
                                }}>
                                    ${Number(project.afs).toLocaleString()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ── FULL DESCRIPTION ── */}
            <section className="local" style={{ maxWidth: "860px", marginInline: "auto" }}>
                <div
                    className="lcard lcard--open"
                    style={{
                        background: bgVar,
                        borderColor: borderVar,
                        padding: "2rem 2.2rem",
                        gap: "1rem",
                        fontSize: ".97rem",
                        lineHeight: 1.85,
                        color: "var(--muted)",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".25rem" }}>
                        <span className={`grow__dot ${meta.dot}`} style={{ width: 12, height: 12 }} />
                        <h2 style={{
                            fontFamily: "var(--ff-head)",
                            fontSize: "1.15rem",
                            fontWeight: 800,
                            color: "var(--ink)",
                        }}>
                            About {project.name}
                        </h2>
                    </div>
                    <div style={{ borderTop: `1px solid ${borderVar}`, paddingTop: "1rem" }}>
                        {parse(project.desc || project.shortDesc)}
                    </div>
                    {project.uri && (
                        <a
                            href={project.uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lcard__toggle"
                            style={{ marginTop: ".5rem", color: accentVar, borderColor: borderVar }}
                        >
                            Visit official website ↗
                        </a>
                    )}
                </div>
            </section>

            {/* ── PHOTO GALLERY ── */}
            {photos.length > 0 && (
                <section id="gallery" className="local" style={{ maxWidth: "var(--max)", marginInline: "auto" }}>
                    <div className="sec-hd">
                        <span className="sec-eye">Gallery</span>
                        <h2 className="sec-h2">Photos</h2>
                    </div>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: photos.length === 1 ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: "1.25rem",
                    }}>
                        {photos.map((photo, i) => (
                            <div key={i} className="lcard" style={{ padding: 0, gap: 0, overflow: "hidden" }}>
                                <div style={{ width: "100%", aspectRatio: "16/10", overflow: "hidden" }}>
                                    <img
                                        src={photo.src}
                                        alt={photo.caption || `${project.name} photo ${i + 1}`}
                                        loading="lazy"
                                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        onError={e => (e.target.parentElement.style.display = "none")}
                                    />
                                </div>
                                {photo.caption && (
                                    <div style={{ padding: "1rem 1.2rem" }}>
                                        <p style={{ fontSize: ".85rem", color: "var(--muted)", lineHeight: 1.65 }}>
                                            {parse(photo.caption)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* ── BACK LINK ── */}
            <div style={{ maxWidth: "var(--max)", marginInline: "auto", padding: "0 var(--pad) clamp(3rem,6vw,5rem)" }}>
                <Link
                    to="/projects"
                    className="lcard__toggle"
                    style={{ textDecoration: "none", display: "inline-block" }}
                >
                    ← Back to all projects
                </Link>
            </div>

        </div>
    );
}